<template>
  <div
    class="signin d-flex flex-column justify-content-center align-items-center h-100 p-4"
  >
    <div class="logo-container d-flex flex-row justify-content-center">
      <HeaderLogo class="mx-auto" />
    </div>
    <form
      class="login-form p-4 p-sm-5 w-100 text-black"
      @submit.prevent="confirmLogin"
    >
      <div v-if="loading" class="d-flex flex-column align-items-center my-3">
        <b-spinner
          style="width: 4rem; height: 4rem"
          variant="primary"
          label="Spinning"
        ></b-spinner>
      </div>
      <template v-else>
        <b-form-group
          label="First Name"
          :state="formErrors.firstName.length == 0 ? null : false"
          :invalid-feedback="formErrors.firstName.join(', ')"
        >
          <b-form-input
            v-model="form.firstName"
            placeholder="First Name"
            class="auth-input bigger-input"
            :state="formErrors.firstName.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Date Of Birth"
          :state="formErrors.dob.length == 0 ? null : false"
          :invalid-feedback="formErrors.dob.join(', ')"
        >
          <date-picker v-model="form.dob" :max="new Date()" />
        </b-form-group>

        <b-form-group
          label="Last 4 SSN"
          :state="formErrors.ssn.length == 0 ? null : false"
          :invalid-feedback="formErrors.ssn.join(', ')"
        >
          <b-form-input
            v-model="form.ssn"
            placeholder="Last 4 SSN"
            maxlength="4"
            minlength="4"
            class="auth-input bigger-input"
            :state="formErrors.ssn.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
        <img
          v-if="captchaImg"
          class="captcha"
          :src="`data:image/png;base64,${captchaImg}`"
          @click="getCaptcha"
          title="Click to change"
        />
        <b-form-group
          v-if="captchaImg"
          label="Type the characters above"
          :state="formErrors.captcha.length == 0 ? null : false"
          :invalid-feedback="formErrors.captcha.join(', ')"
        >
          <b-form-input
            v-model="form.captcha"
            class="auth-input bigger-input"
            :state="formErrors.captcha.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
        <div v-if="error">
          <small class="text-danger">{{ error }}</small>
        </div>

        <div class="d-flex flex-row justify-content-center">
          <button class="btn btn-primary px-5" type="submit">
            Sign In
          </button>
        </div>
      </template>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import HeaderLogo from "../../views/HeaderLogo.vue";
import DatePicker from "../controls/DatePicker.vue";
import { setToken } from "../../helpers/session";
import { validateRequired } from "../../helpers/validation";
import authServer from "../../server/auth";

export default {
  name: "Login",
  components: {
    HeaderLogo,
    DatePicker
  },
  data() {
    return {
      loading: false,
      form: {
        firstName: "",
        ssn: "",
        dob: undefined,
        id: null,
        captcha: ""
      },
      formErrors: {
        firstName: [],
        ssn: [],
        dob: [],
        captcha: []
      },
      error: "",
      captchaImg: null,
      loginAttempts: 0
    };
  },
  async mounted() {
    this.loginAttempts = await authServer.getLoginAttempts();
    if (this.loginAttempts >= 3) {
      this.getCaptcha();
    }
  },
  methods: {
    ...mapActions("layout", ["fetchCurrentUser"]),
    validateLogin() {
      let withoutErrors = true;

      this.formErrors.firstName = [];
      if (!validateRequired(this.form.firstName)) {
        this.formErrors.firstName.push("This field is required");
        withoutErrors = false;
      }
      this.formErrors.ssn = [];
      if (!validateRequired(this.form.ssn)) {
        this.formErrors.ssn.push("This field is required");
        withoutErrors = false;
      }
      this.formErrors.dob = [];
      if (!validateRequired(this.form.dob)) {
        this.formErrors.dob.push("This field is required");
        withoutErrors = false;
      }
      this.formErrors.captcha = [];
      if (this.captchaImg && !validateRequired(this.form.captcha)) {
        this.formErrors.captcha.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async confirmLogin() {
      this.loading = true;
      const result = await this.validateLogin();
      if (result) {
        this.error = "";
        const authResult = await authServer.authenticate({
          firstName: this.form.firstName,
          dateOfBirth: this.form.dob,
          lastSsnDigits: this.form.ssn,
          captchaId: this.form.id,
          captchaCode: this.form.captcha
        });
        if (authResult.errorCode) {
          this.loginAttempts = authResult.attempts;
          this.error = authResult.message;
          if (this.loginAttempts >= 3) {
            this.getCaptcha();
          }
        } else {
          await setToken(authResult.token);
          await this.fetchCurrentUser();
          const redirect = this.$route.query.redirect;
          const promise = this.$router.push("/");
          if (redirect) {
            promise.then(() => this.$router.push(redirect).catch(() => {}));
          }
        }
      }
      this.loading = false;
    },
    async getCaptcha() {
      let captcha = await authServer.getCaptchaImg();
      this.form.id = captcha.id;
      this.captchaImg = captcha.image;
    }
  }
};
</script>

<style lang="scss" scoped>
.signin {
  position: relative;
  background: linear-gradient(
    to bottom,
    #13609b,
    #208de2 47.5%,
    #f5f6f9 47.5%,
    #f5f6f9
  );

  .logo-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, calc(-50% - 235px));
  }
}

.login-form {
  max-width: 500px;

  .lang-select {
    float: left;
    font-size: 0.7em;
    max-width: 100px;
    margin-top: 2em;
  }
}

.captcha {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 50%;
  cursor: pointer;
}
</style>
